// Multi-zone stage renderer (docs/04 §Zones).
//
// Presents the SAME surface app.ts already drove on a single PlaybackEngine —
// setManifest / start / stop / current-item / freeze probes — but fans out to one
// player-core engine PER zone when a manifest declares a layout. player-core is
// untouched (a zone-agnostic single-timeline renderer); all the composition lives
// here in the app, exactly as the contract requires.
//
// No-zones is the invariant: with no `zones` the single engine renders directly
// on the root element, producing DOM byte-identical to today (so previews and the
// 9 existing e2e specs are unaffected). Zones only add cost when present.
//
// The layout is rebuilt only when its geometry changes (`zoneLayoutKey`); a plain
// content swap reuses the live engines so playback (crossfade, atomic swap) is
// never interrupted. The overlay layer is composed ONCE above all zones.

import { OverlayLayer, PlaybackEngine } from '@seencast/player-core';
import type { MediaSource, PlaybackConfig, PlayerLogger } from '@seencast/player-core';
import type { Manifest } from './types.ts';
import { hasZones, zoneLayoutKey, zonePlan } from './zones.ts';

interface ZoneEngine {
  engine: PlaybackEngine;
  /** The element the engine renders into (root in single mode, .zone-stage in zones). */
  el: HTMLElement;
  isMain: boolean;
}

export class ZoneStage {
  private engines: ZoneEngine[] = [];
  /** Top-level overlay, composed above all zones. Only used in zones mode. */
  private overlay: OverlayLayer | null = null;
  private layoutKey: string | null = null;
  private started = false;

  constructor(
    private root: HTMLElement,
    private media: MediaSource,
    private log: PlayerLogger,
    private config: PlaybackConfig = {},
  ) {
    // Eager single engine so a freshly paired device shows the idle screen before
    // its first manifest — matching today's behavior exactly.
    this.buildSingle();
  }

  /** Apply a manifest: reuse the live engines on a content swap, rebuild on a layout change. */
  setManifest(m: Manifest): void {
    const key = zoneLayoutKey(m);
    if (key !== this.layoutKey) {
      this.rebuild(m, key);
      return;
    }
    // Same layout → swap content into the existing engines (no interruption).
    if (hasZones(m)) {
      const plan = zonePlan(m);
      plan.forEach((p, i) => this.engines[i]?.engine.setManifest(p.manifest));
      this.overlay?.apply(m.overlay ?? null);
    } else {
      this.engines[0]?.engine.setManifest(m);
    }
  }

  start(): void {
    this.started = true;
    for (const z of this.engines) z.engine.start();
  }

  stop(): void {
    this.started = false;
    for (const z of this.engines) z.engine.stop();
  }

  /** The main zone's on-screen item id (the primary content — used for the heartbeat). */
  getCurrentItemId(): string | null {
    return this.mainEngine()?.getCurrentItemId() ?? null;
  }

  /** Worst (largest) staleness across all zones — any frozen zone trips the watchdog. */
  msSinceFrame(): number {
    let max = 0;
    for (const z of this.engines) max = Math.max(max, z.engine.msSinceFrame());
    return max;
  }

  getTick(): number {
    return this.mainEngine()?.getTick() ?? 0;
  }

  /** Test seam (?mock=1): backdate the main engine's frame clock so the watchdog sees a freeze. */
  forceFrozenForTest(): void {
    (this.mainEngine() as unknown as { forceFrozenForTest(): void } | undefined)?.forceFrozenForTest();
  }

  /** The DOM element hosting the main zone (for best-effort DOM snapshot capture). */
  mainStageEl(): HTMLElement {
    return this.mainZoneEngine()?.el ?? this.root;
  }

  // --- internal ---------------------------------------------------------------

  private mainEngine(): PlaybackEngine | null {
    return this.mainZoneEngine()?.engine ?? this.engines[0]?.engine ?? null;
  }

  private mainZoneEngine(): ZoneEngine | null {
    return this.engines.find((z) => z.isMain) ?? this.engines[0] ?? null;
  }

  private rebuild(m: Manifest, key: string): void {
    for (const z of this.engines) z.engine.stop();
    this.engines = [];
    this.overlay = null;
    this.root.replaceChildren();
    this.layoutKey = key;

    if (!hasZones(m)) {
      this.buildSingle();
      this.engines[0]!.engine.setManifest(m);
    } else {
      this.buildZones(m);
    }
    if (this.started) for (const z of this.engines) z.engine.start();
  }

  /** One engine on the root element — identical DOM to the pre-zones player. */
  private buildSingle(): void {
    const engine = new PlaybackEngine(this.root, this.media, this.log, this.config);
    this.engines = [{ engine, el: this.root, isMain: true }];
    this.layoutKey = 'single';
  }

  private buildZones(m: Manifest): void {
    // The root becomes the size container for the shared overlay's cq units.
    this.root.classList.add('stage');
    for (const p of zonePlan(m)) {
      const zoneEl = document.createElement('div');
      zoneEl.className = 'zone';
      zoneEl.style.left = `${p.rect.x}%`;
      zoneEl.style.top = `${p.rect.y}%`;
      zoneEl.style.width = `${p.rect.w}%`;
      zoneEl.style.height = `${p.rect.h}%`;
      const stageEl = document.createElement('div');
      stageEl.className = 'zone-stage';
      zoneEl.appendChild(stageEl);
      this.root.appendChild(zoneEl);
      const engine = new PlaybackEngine(stageEl, this.media, this.log, this.config);
      engine.setManifest(p.manifest);
      this.engines.push({ engine, el: stageEl, isMain: p.isMain });
    }
    // Overlay composed ONCE, appended last → stacks above every zone.
    this.overlay = new OverlayLayer(this.root, this.media);
    this.overlay.apply(m.overlay ?? null);
  }
}
